import { Dropdown, message } from 'antd';
import { CouponIcon, DeleteIcon, DuplicateIcon, EditOutline, EyeIcon, MoreOutline } from '../Icons';
import { Heading } from '../Heading';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { JustifyContent, OrderStatusTab, PermissionName } from '@/types';
import { useHandleCancelOrder, useOpenEntityTicketModal } from '@/hooks';
import { ROUTE } from '@/config';
import { ResolveModal } from '../ResolveModal';
import { useResolveModalStore } from '@/hooks';
import { OrderDataType } from '@/hooks/useOrderData';
import { ItemType } from 'antd/es/menu/interface';
import { useDuplicateOrder } from '@/hooksApi';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigateWithPrevPageState } from '@/hooks/navigateWithPrevPageState';
import { TicketEntityIDFieldEnum } from '@/constants';
import { getTicketEntityAndPageRoute, formatDateByDayjs } from '@/utils';
import usePermission from '@/hooks/usePermission';

export interface ActionProps {
  uid?: string;
  activeTab?: OrderStatusTab;
  cancelLabel?: string;

  orderData?: OrderDataType;
  savePrevPage?: boolean;
  button?: {
    label?: string;
    icon?: ReactNode;
    disabled?: boolean;
    onClick?: (() => void) | 'openResolveModal';
    navigateLink?: string;
    isLoading?: boolean;
    variant?: 'primary' | 'muted';
  };
  menuItems?: OrderMenuItemsEnum[];
  customMenuItems?: CustomMenuItemData_ActionTable[];
  ticketEntityPlatformCode?: string;
  wrapperJustify?: JustifyContent;
}

export interface CustomMenuItemData_ActionTable {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  leadingIcon?: ReactNode;
  loading?: boolean;
  key: string;
}

interface MenuItemData {
  key: string;
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export enum OrderMenuItemsEnum {
  EDIT_ORDER = 'edit',
  CANCEL_ORDER = 'cancel',
  DISABLE_CANCEL_ORDER = 'disable-cancel',
  VIEW_ORDER = 'view',
  DUPLICATE_ORDER = 'duplicate',
  CREATE_TICKET = 'create_ticket',
}

const ActionsTable = ({
  button,
  menuItems,
  customMenuItems,
  uid,
  activeTab,
  cancelLabel,
  orderData,
  savePrevPage,
  ticketEntityPlatformCode,
  wrapperJustify,
}: ActionProps) => {
  const [resolveModalOpen, setResolveModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigateWithPrevPageState = useNavigateWithPrevPageState();
  const { hasPermission } = usePermission();

  // Zustand store setters
  const setFullName = useResolveModalStore((state) => state.setFullName);
  const setPhone = useResolveModalStore((state) => state.setPhone);
  const setOrderValue = useResolveModalStore((state) => state.setOrderValue);
  const setAddressTitle = useResolveModalStore((state) => state.setAddressTitle);

  const { openEntityTicketModal } = useOpenEntityTicketModal(
    getTicketEntityAndPageRoute(ticketEntityPlatformCode)?.entityId ??
      TicketEntityIDFieldEnum.ORDER,
  );

  // mutate cancel or delete order
  const { handleCancelOrder, isLoading: isCanceling } = useHandleCancelOrder(
    uid || '',
    activeTab || OrderStatusTab.READY_TO_SHIP,
    orderData?.status?.slug,
  );

  const { mutate: duplicateOrder, isLoading: isDuplicating } = useDuplicateOrder();

  if (orderData && resolveModalOpen && uid && orderData?.merchant_action_required) {
    setFullName(orderData?.customerName || '');
    setPhone(orderData.phone || '');
    setOrderValue(String(orderData?.orderValue || 0));
    setAddressTitle(orderData?.pickupLocation || '');
  }

  const MENU_ORDER: OrderMenuItemsEnum[] = [
    OrderMenuItemsEnum.VIEW_ORDER,
    OrderMenuItemsEnum.EDIT_ORDER,
    OrderMenuItemsEnum.CREATE_TICKET,
    OrderMenuItemsEnum.DUPLICATE_ORDER,
    OrderMenuItemsEnum.CANCEL_ORDER,
    OrderMenuItemsEnum.DISABLE_CANCEL_ORDER,
  ];

  const orderMenuItems: { [key in OrderMenuItemsEnum]: MenuItemData } = {
    [OrderMenuItemsEnum.EDIT_ORDER]: {
      key: OrderMenuItemsEnum.EDIT_ORDER,
      label: (
        <Heading
          title={t('edit')}
          leadingIcon={<EditOutline />}
          rotation={false}
          disabled={!hasPermission(PermissionName.UPDATE_ORDER)}
        />
      ),
      disabled: !hasPermission(PermissionName.UPDATE_ORDER),
      onClick: () => {
        if (hasPermission(PermissionName.UPDATE_ORDER)) {
          navigateWithPrevPageState(generatePath(ROUTE.ORDER_EDIT, { orderUid: uid }));
        }
      },
    },
    [OrderMenuItemsEnum.VIEW_ORDER]: {
      key: OrderMenuItemsEnum.VIEW_ORDER,
      label: (
        <Heading
          title={t('view')}
          leadingIcon={<EyeIcon stroke='#fff' fill='#fff' />}
          rotation={false}
        />
      ),
      onClick: () => navigateWithPrevPageState(`${ROUTE.ORDER_DETAILS}/${uid}`),
    },
    [OrderMenuItemsEnum.CANCEL_ORDER]: {
      key: OrderMenuItemsEnum.CANCEL_ORDER,
      label: (
        <Heading
          title={cancelLabel ?? t('cancel')}
          leadingIcon={<DeleteIcon />}
          loading={isCanceling}
          disabled={
            !hasPermission(cancelLabel ? PermissionName.DELETE_ORDER : PermissionName.CANCEL_ORDER)
          }
        />
      ),
      disabled: !hasPermission(
        cancelLabel ? PermissionName.DELETE_ORDER : PermissionName.CANCEL_ORDER,
      ),
      onClick: async () => {
        if (
          hasPermission(cancelLabel ? PermissionName.DELETE_ORDER : PermissionName.CANCEL_ORDER)
        ) {
          handleCancelOrder();
        }
      },
    },
    [OrderMenuItemsEnum.DISABLE_CANCEL_ORDER]: {
      key: OrderMenuItemsEnum.CANCEL_ORDER,
      label: (
        <Heading
          title={cancelLabel ?? t('cancel')}
          leadingIcon={<DeleteIcon />}
          disabled={true}
          parentClassName='cursor-not-allowed'
        />
      ),
      disabled: true,
      onClick: () => {},
    },
    [OrderMenuItemsEnum.DUPLICATE_ORDER]: {
      key: OrderMenuItemsEnum.DUPLICATE_ORDER,
      label: (
        <Heading
          title={t('duplicate_order')}
          leadingIcon={<DuplicateIcon />}
          loading={isDuplicating}
          disabled={!hasPermission(PermissionName.CREATE_ORDER)}
        />
      ),
      disabled: !hasPermission(PermissionName.CREATE_ORDER),
      onClick: () => {
        if (hasPermission(PermissionName.CREATE_ORDER)) {
          duplicateOrder(uid || '', {
            onSuccess: () => {
              message.success(t('operation_completed_successfully'));
              queryClient.invalidateQueries(['orders']);
              queryClient.invalidateQueries(['order-count']);
            },
            onError: (error: any) => {
              const errorCode = error?.response?.data?.code;
              const errorMessage = error?.response?.data?.message || '';
              if (errorCode === 760001 || errorMessage.includes('no matching carrier found')) {
                message.error(t('no_carrier_matched'));
              } else {
                message.error(errorMessage || t('operation_failed'));
              }
            },
          });
        }
      },
    },
    [OrderMenuItemsEnum.CREATE_TICKET]: {
      key: OrderMenuItemsEnum.CREATE_TICKET,
      label: (
        <Heading
          title={t('create_ticket')}
          leadingIcon={<CouponIcon width={16} height={16} color='#939496' />}
          loading={isDuplicating}
          disabled={!hasPermission(PermissionName.CREATE_TICKET)}
        />
      ),
      disabled: !hasPermission(PermissionName.CREATE_TICKET),
      onClick: async () => {
        if (hasPermission(PermissionName.CREATE_TICKET)) {
          openEntityTicketModal(ticketEntityPlatformCode, uid);
        }
      },
    },
  };

  const getCustomMenuItems = (customMenuItems?: CustomMenuItemData_ActionTable[]): ItemType[] => {
    return getFilteredCustomMenuItems(customMenuItems);
  };

  const openResolveModal = () => {
    setResolveModalOpen(true);
  };

  const dropDownDividerItem: ItemType = {
    type: 'divider',
  };

  const getMenuItems = () => {
    const customItems = getCustomMenuItems(customMenuItems);
    const items: ItemType[] = [];

    if (menuItems && menuItems.length > 0) {
      // Filter out VIEW_ORDER from menu items as it will be shown as separate button
      const filteredMenuItems = menuItems.filter((item) => item !== OrderMenuItemsEnum.VIEW_ORDER);
      const sortedMenuItems = [...filteredMenuItems].sort(
        (a, b) => MENU_ORDER.indexOf(a) - MENU_ORDER.indexOf(b),
      );

      sortedMenuItems.forEach((item, index) => {
        const itemWithSeparator: ItemType[] =
          index === sortedMenuItems.length - 1
            ? [orderMenuItems[item] as ItemType]
            : [orderMenuItems[item] as ItemType, dropDownDividerItem];
        items.push(...itemWithSeparator);
      });
    }

    return [...items, ...customItems];
  };

  // Check if VIEW_ORDER exists in menuItems or customMenuItems
  const hasViewOption = () => {
    const hasViewInMenuItems = menuItems?.includes(OrderMenuItemsEnum.VIEW_ORDER);
    const hasViewInCustomItems = customMenuItems?.some(
      (item) =>
        item.key === 'view' || item.key === '1' || item.label.toLowerCase().includes('view'),
    );
    return hasViewInMenuItems || hasViewInCustomItems;
  };

  // Get view action handler
  const getViewAction = () => {
    if (menuItems?.includes(OrderMenuItemsEnum.VIEW_ORDER)) {
      return orderMenuItems[OrderMenuItemsEnum.VIEW_ORDER].onClick;
    }

    const viewCustomItem = customMenuItems?.find(
      (item) =>
        item.key === 'view' || item.key === '1' || item.label.toLowerCase().includes('view'),
    );
    return viewCustomItem?.onClick;
  };

  // Filter out view option from custom menu items
  const getFilteredCustomMenuItems = (
    customMenuItems?: CustomMenuItemData_ActionTable[],
  ): ItemType[] => {
    if (!customMenuItems) return [];

    const filteredItems = customMenuItems.filter(
      (item) =>
        !(item.key === 'view' || item.key === '1' || item.label.toLowerCase().includes('view')),
    );

    const items: ItemType[] = [];
    filteredItems.forEach((item, index) => {
      items.push({
        key: item.key,
        label: (
          <Heading
            title={item.label}
            loading={item.loading}
            leadingIcon={item.leadingIcon}
            disabled={item.disabled}
          />
        ),
        disabled: item.disabled,
        onClick: item.onClick,
      });
      if (index !== filteredItems.length - 1) items.push(dropDownDividerItem);
    });

    return items;
  };

  // Check if button is resolve button and user has permission
  const shouldShowButton =
    button &&
    (button.onClick !== 'openResolveModal' || hasPermission(PermissionName.RESOLVE_OR_REQUEST_RTO));

  const btnClickHandler = button?.navigateLink
    ? () => navigateWithPrevPageState(button.navigateLink || '')
    : button?.onClick === 'openResolveModal'
      ? openResolveModal
      : button?.onClick ?? undefined;

  const isMuted = button?.variant === 'muted';
  const labelledBtnClass = isMuted
    ? 'flex items-center gap-1.5 h-7 px-3 rounded-lg bg-[rgb(251,250,232)] border border-solid border-[rgb(201,194,74)] text-[rgb(57,57,60)] text-[11px] font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(246,244,214)] transition-colors whitespace-nowrap'
    : 'flex items-center gap-1.5 h-7 px-3 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors whitespace-nowrap';

  return (
    <div className='flex items-center gap-1.5' style={{ justifyContent: wrapperJustify ?? 'end' }} onClick={(e) => e.stopPropagation()}>
      {shouldShowButton && (
        button.label ? (
          <button
            data-testid='action-button'
            type='button'
            className={labelledBtnClass}
            onClick={btnClickHandler as (() => void) | undefined}
            disabled={button.disabled || button.isLoading}
          >
            {button.isLoading ? (
              <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
            ) : (
              button.icon
            )}
            {button.label}
          </button>
        ) : (
          <button
            data-testid='action-button'
            type='button'
            className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors'
            onClick={btnClickHandler as (() => void) | undefined}
            disabled={button.disabled || button.isLoading}
          >
            {button.isLoading ? (
              <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
            ) : (
              button.icon
            )}
          </button>
        )
      )}

      {hasViewOption() && (
        <button
          data-testid='view-button'
          type='button'
          className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          onClick={getViewAction()}
        >
          <EyeIcon width={16} height={16} />
        </button>
      )}

      {((menuItems && menuItems.length > 0) ||
        (customMenuItems && customMenuItems.length > 0)) && (
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
        >
          <button
            data-testid='action-dropdown'
            type='button'
            className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          >
            <MoreOutline width={18} height={18} />
          </button>
        </Dropdown>
      )}

      <ResolveModal
        data-testid='resolve-modal'
        visible={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        orderId={uid}
        platformCode={orderData?.platform_code}
        comment={orderData?.merchant_action_required_text}
        carrierComment={orderData?.carrier_message && orderData.carrier_message !== '---' ? orderData.carrier_message : ''}
        commentDate={formatDateByDayjs(orderData?.orderDateRaw, 'D MMM YYYY, HH:mm')}
        carrierCommentDate={formatDateByDayjs(orderData?.orderDateRaw, 'D MMM YYYY, HH:mm')}
      />
    </div>
  );
};

export default ActionsTable;
